import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { useEstimate } from "../context/EstimateContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const genId = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

const GREETING = "Hi! I'm the Apex Home Pro assistant. Ask me about remodeling, our services in the Lehigh Valley, or how to get a free estimate. 👷";

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(genId);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const { openEstimate } = useEstimate();

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5).trim());
          if (payload.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + payload.delta };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I had trouble connecting. Please call us at (484) 632-5956." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-widget-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-label="Open chat assistant"
        className="fixed bottom-6 left-6 z-40 grid place-items-center h-14 w-14 rounded-full bg-[var(--apex-primary)] hover:bg-slate-800 text-white shadow-2xl transition-colors"
      >
        {open ? <X size={22} /> : <MessageCircle size={24} />}
      </button>

      {open && (
        <div
          data-testid="chat-window"
          className="fixed bottom-24 left-6 z-40 w-[calc(100vw-3rem)] sm:w-[380px] h-[520px] max-h-[70vh] rounded-lg bg-white/90 backdrop-blur-xl border border-slate-200 shadow-2xl flex flex-col overflow-hidden animate-fade-up"
        >
          <div className="bg-[var(--apex-primary)] px-4 py-3.5 flex items-center gap-3">
            <span className="grid place-items-center h-9 w-9 rounded-full bg-[var(--apex-accent)]"><Sparkles size={17} className="text-white" /></span>
            <div>
              <div className="font-display font-bold text-white leading-none">Apex Assistant</div>
              <div className="text-[11px] text-green-400 mt-1 flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-green-400" /> Online now</div>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto apex-scroll p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[82%] rounded-lg px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[var(--apex-accent)] text-white rounded-br-sm"
                      : "bg-slate-100 text-slate-800 rounded-bl-sm"
                  }`}
                >
                  {m.content || <span className="inline-flex gap-1"><span className="h-2 w-2 bg-slate-400 rounded-full animate-bounce" /><span className="h-2 w-2 bg-slate-400 rounded-full animate-bounce [animation-delay:0.15s]" /><span className="h-2 w-2 bg-slate-400 rounded-full animate-bounce [animation-delay:0.3s]" /></span>}
                </div>
              </div>
            ))}
          </div>

          <div className="px-4 py-2 border-t border-slate-200">
            <button data-testid="chat-estimate-cta" onClick={openEstimate} className="w-full text-sm font-semibold text-[var(--apex-accent)] hover:text-[var(--apex-accent-dark)] py-1.5 transition-colors">
              Get a free estimate →
            </button>
          </div>

          <div className="p-3 border-t border-slate-200 flex items-center gap-2">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about your remodel..."
              className="flex-1 h-11 rounded-sm border border-slate-300 px-3 text-sm outline-none focus:border-[var(--apex-accent)] focus:ring-1 focus:ring-[var(--apex-accent)]"
            />
            <button
              data-testid="chat-send-btn"
              onClick={send}
              disabled={busy}
              className="grid place-items-center h-11 w-11 rounded-sm bg-[var(--apex-accent)] hover:bg-[var(--apex-accent-dark)] text-white transition-colors disabled:opacity-60"
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
